import { AnalyticsEvents } from "analytics/analytics";
import { Pool as PoolSDK } from "@antefinance/ante-sdk";
import { SendTransaction } from "../fetchAntePoolsPublic";
import { Web3Utils } from "utils/web3-utils";
import { BigNumber } from "ethers";
import { formatUnits } from "@ethersproject/units";
import { AnteNumber, BasePool, PoolSideInfo } from "types";
import { formatAnteNumber } from "utils/utils";

const fetchOnChainPool = async (
  offChainPool: BasePool,
  publicPoolInstance: PoolSDK.AntePoolInstance,
  poolInstance: PoolSDK.AntePoolInstance,
  sendTransaction: SendTransaction,
  account: string
) => {
  const token = offChainPool.token;

  const methods = [
    {
      method: () => publicPoolInstance.getStakingDetails(),
      transform: (value: PoolSDK.PoolSideInfo): PoolSideInfo => ({
        totalAmount: formatAnteNumber(value.totalAmount),
        decayMultiplier: formatAnteNumber(value.decayMultiplier),
        numUsers: value.numUsers.toNumber(),
      }),
      valueName: "stakingInfo",
    },
    {
      method: () => publicPoolInstance.getChallengerDetails(),
      transform: (value: PoolSDK.PoolSideInfo): PoolSideInfo => ({
        totalAmount: formatAnteNumber(value.totalAmount),
        decayMultiplier: formatAnteNumber(value.decayMultiplier),
        numUsers: value.numUsers.toNumber(),
      }),
      valueName: "challengerInfo",
    },
    {
      method: () => publicPoolInstance.getTotalPendingWithdraw(),
      valueName: "totalPendingWithdraw",
      transform: (value: BigNumber): AnteNumber =>
        formatAnteNumber(value, token.decimals),
    },
    {
      method: () => publicPoolInstance.getUserStoredBalance(account, false),
      valueName: "stakedBalance",
      transform: (value: BigNumber): AnteNumber =>
        formatAnteNumber(value, token.decimals),
    },
    {
      method: () => publicPoolInstance.getUserStoredBalance(account, true),
      valueName: "challengedBalance",
      transform: (value: BigNumber): AnteNumber =>
        formatAnteNumber(value, token.decimals),
    },
    {
      method: () => publicPoolInstance.getPendingWithdrawAmount(account),
      valueName: "pendingWithdrawAmount",
      transform: (value: BigNumber): AnteNumber =>
        formatAnteNumber(value, token.decimals),
    },
    {
      method: () => publicPoolInstance.getPendingFailure(),
      valueName: "pendingFailure",
    },
    {
      method: () => publicPoolInstance.getTimesVerified(),
      transform: (value: BigNumber) => Number(value.toString()),
      valueName: "numTimesVerified",
    },
    {
      method: () => publicPoolInstance.getLastVerifiedBlock(),
      transform: (value: BigNumber) => Number(value.toString()),
      valueName: "lastVerifiedBlock",
    },
    {
      method: () => publicPoolInstance.getVerifier(),
      valueName: "verifier",
    },
    {
      method: () => publicPoolInstance.getUserStartAmount(account, false),
      valueName: "stakedStartAmount",
      transform: (value: BigNumber): AnteNumber =>
        formatAnteNumber(value, token.decimals),
    },
    {
      method: () => publicPoolInstance.getUserStartAmount(account, true),
      valueName: "challengedStartAmount",
      transform: (value: BigNumber): AnteNumber =>
        formatAnteNumber(value, token.decimals),
    },
    {
      method: () => publicPoolInstance.getVerifierBounty(),
      valueName: "verifierBounty",
      transform: (value: BigNumber): AnteNumber =>
        formatAnteNumber(value, token.decimals),
    },
    {
      method: () => publicPoolInstance.getCheckTestAllowedBlock(account),
      valueName: "checkTestAllowedBlock",
      transform: (value: BigNumber) => Number(value.toString()),
    },
  ];

  const data = await Web3Utils.batch(methods);
  const {
    stakingInfo,
    challengerInfo,
    totalPendingWithdraw,
    stakedBalance,
    challengedBalance,
    pendingWithdrawAmount,
    pendingFailure,
    numTimesVerified,
    lastUpdateBlock,
    lastVerifiedBlock,
    verifier,
    stakedStartAmount,
    challengedStartAmount,
    verifierBounty,
    checkTestAllowedBlock,
  } = data;

  const secondaryMethods = [
    {
      method: () => publicPoolInstance.getPendingWithdrawAllowedTime(account),
      transform: (timestampInSeconds: string) =>
        Number(timestampInSeconds.toString()),
      valueName: "pendingWithdrawAllowedTime",
      skip: pendingWithdrawAmount?.bn.eq(0),
    },
    {
      method: () => publicPoolInstance.getChallengerPayout(account),
      transform: (value: BigNumber): AnteNumber =>
        formatAnteNumber(value, token.decimals),
      valueName: "challengerPayout",
      skip: challengedStartAmount?.bn.eq(0),
    },
  ];

  const secondaryData = await Web3Utils.batch(secondaryMethods);
  const { pendingWithdrawAllowedTime, challengerPayout } = secondaryData;

  const provider = poolInstance.getProvider();

  const stake = async (value: BigNumber): Promise<any> =>
    sendTransaction(
      async () =>
        poolInstance.stake(
          { amount: value },
          await Web3Utils.getWeb3Signer(provider, account)
        ),
      "Staking...",
      {
        eventName: AnalyticsEvents.stake,
        options: {
          value: Number(formatUnits(value, token.decimals)),
          testName: offChainPool.anteTestName,
        },
      }
    );

  const challenge = async (value: BigNumber): Promise<any> =>
    sendTransaction(
      async () =>
        poolInstance.challenge(
          { amount: value },
          await Web3Utils.getWeb3Signer(provider, account)
        ),
      "Challenging...",
      {
        eventName: AnalyticsEvents.challenge,
        options: {
          value: Number(formatUnits(value, token.decimals)),
          testName: offChainPool.anteTestName,
        },
      }
    );

  const withdrawChallenge = async (): Promise<any> =>
    sendTransaction(
      async () =>
        poolInstance.unstakeAll(
          { isChallenger: true },
          await Web3Utils.getWeb3Signer(provider, account)
        ),
      "Withdrawing challenge...",
      {
        eventName: AnalyticsEvents.withdrawChallenge,
        options: { testName: offChainPool.anteTestName },
      }
    );

  const claim = async (): Promise<any> =>
    sendTransaction(
      async () =>
        poolInstance.claim(await Web3Utils.getWeb3Signer(provider, account)),
      "Claiming reward...",
      {
        eventName: AnalyticsEvents.claim,
        options: { testName: offChainPool.anteTestName },
      }
    );

  const checkTest = async (): Promise<any> =>
    sendTransaction(
      async () =>
        poolInstance.checkTest(
          await Web3Utils.getWeb3Signer(provider, account)
        ),
      "Verifying test...",
      {
        eventName: AnalyticsEvents.verifyTest,
        options: { testName: offChainPool.anteTestName },
      }
    );

  const unstakeAll = async (isChallenger: boolean): Promise<any> =>
    sendTransaction(
      async () =>
        poolInstance.unstakeAll(
          { isChallenger },
          await Web3Utils.getWeb3Signer(provider, account)
        ),
      isChallenger ? "Unstaking challenger stake..." : "Initiating unstake...",
      {
        eventName: isChallenger
          ? AnalyticsEvents.withdrawChallenge
          : AnalyticsEvents.initiateUnstake,
        options: { testName: offChainPool.anteTestName },
      }
    );

  const withdrawStake = async (): Promise<any> =>
    sendTransaction(
      async () =>
        poolInstance.withdrawStake(
          await Web3Utils.getWeb3Signer(provider, account)
        ),
      "Withdrawing stake...",
      {
        eventName: AnalyticsEvents.withdrawStake,
        options: { testName: offChainPool.anteTestName },
      }
    );

  const cancelPendingWithdraw = async (): Promise<any> =>
    sendTransaction(
      async () =>
        poolInstance.cancelPendingWithdraw(
          await Web3Utils.getWeb3Signer(provider, account)
        ),
      "Cancelling withdrawal...",
      {
        eventName: AnalyticsEvents.cancelWithdraw,
        options: { testName: offChainPool.anteTestName },
      }
    );

  return {
    stakingInfo,
    challengerInfo,
    totalPendingWithdraw,
    stakedBalance,
    challengedBalance,
    pendingWithdrawAmount,
    pendingWithdrawAllowedTime,
    pendingFailure,
    numTimesVerified,
    lastUpdateBlock,
    lastVerifiedBlock,
    verifier,
    challengerPayout,
    stakedStartAmount,
    challengedStartAmount,
    verifierBounty,
    checkTestAllowedBlock,
    sendTransaction,
    stake,
    challenge,
    withdrawChallenge,
    claim,
    checkTest,
    unstakeAll,
    withdrawStake,
    cancelPendingWithdraw,
  };
};

export default fetchOnChainPool;
